import React from 'react'
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  Skeleton,
  useTheme,
  alpha
} from '@mui/material'
import {
  Male as MaleIcon,
  Female as FemaleIcon,
  CalendarMonth as CalendarIcon,
  Badge as BadgeIcon,
  LocalHospital as StudyIcon,
  Description as ReportIcon,
  Folder as DocumentIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'
import { PatientWithHistory } from '../../../types/api'

interface PatientHeroCardProps {
  patient: PatientWithHistory | null
  loading?: boolean
  /** Callbacks pentru click pe stats */
  onStudiesClick?: () => void
  onReportsClick?: () => void
  onDocumentsClick?: () => void
  /** Action buttons to render on the right */
  actionButtons?: React.ReactNode
}

/**
 * PatientHeroCard - Card principal cu informațiile pacientului (COMPACT)
 * 
 * Design compact:
 * - Avatar mai mic (64px)
 * - Padding-uri reduse
 * - Stats clickabile în footer
 */
const PatientHeroCard: React.FC<PatientHeroCardProps> = ({ 
  patient, 
  loading,
  onStudiesClick,
  onReportsClick,
  onDocumentsClick,
  actionButtons
}) => {
  const theme = useTheme()
  const { designSystem } = theme
  const colors = theme.palette.mode === 'dark' 
    ? designSystem.colors.dark 
    : designSystem.colors.light

  // Calculează vârsta din data nașterii
  const calculateAge = (dob: string | undefined): string => {
    if (!dob) return ''
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return `${age} ani`
  }

  // Formatează data nașterii
  const formatDOB = (dob: string | undefined): string => {
    if (!dob) return 'N/A'
    return new Date(dob).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  // Calculează "acum X zile" pentru ultimul studiu
  const getRelativeTime = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Astăzi'
    if (diffDays === 1) return 'Ieri'
    if (diffDays < 7) return `Acum ${diffDays} zile`
    if (diffDays < 30) return `Acum ${Math.floor(diffDays / 7)} săpt.`
    if (diffDays < 365) return `Acum ${Math.floor(diffDays / 30)} luni`
    return `Acum ${Math.floor(diffDays / 365)} ani`
  }

  // Generează inițialele pentru avatar
  const getInitials = (firstName: string, lastName: string): string => {
    return `${lastName?.charAt(0) || ''}${firstName?.charAt(0) || ''}`.toUpperCase()
  }

  // Skeleton loading state - COMPACT
  if (loading || !patient) {
    return (
      <Box
        sx={{
          backgroundColor: colors.background.paper,
          borderRadius: `${designSystem.components.card.borderRadius}px`,
          border: `1px solid ${colors.border.default}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Skeleton variant="circular" width={56} height={56} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="50%" height={28} />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          borderTop: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated 
        }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" sx={{ flex: 1, height: 48 }} />
          ))}
        </Box>
      </Box>
    )
  }

  const sexIcon = patient.sex === 'M' ? <MaleIcon /> : patient.sex === 'F' ? <FemaleIcon /> : null
  const sexLabel = patient.sex === 'M' ? 'M' : patient.sex === 'F' ? 'F' : '?'
  const sexColor = patient.sex === 'M' ? '#4299e1' : patient.sex === 'F' ? '#ed64a6' : colors.text.secondary

  return (
    <Box
      sx={{
        backgroundColor: colors.background.paper,
        borderRadius: `${designSystem.components.card.borderRadius}px`,
        border: `1px solid ${colors.border.default}`,
        boxShadow: designSystem.shadows.sm,
        overflow: 'hidden',
      }}
    >
      {/* Main Content - RESPONSIVE */}
      <Box sx={{ p: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'flex-start' }}>
          {/* Avatar - mini */}
          <Avatar
            sx={{
              width: 24,
              height: 24,
              fontSize: '0.6rem',
              fontWeight: 600,
              backgroundColor: patient.sex === 'M' ? '#4299e1' : patient.sex === 'F' ? '#ed64a6' : colors.accent.primary,
              color: '#fff',
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            {getInitials(patient.first_name, patient.last_name)}
          </Avatar>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* First Row: Name + Status + Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap'
            }}>
              {/* Name + Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {patient.last_name} {patient.first_name}
                </Typography>
                
                <Chip
                  label="Activ"
                  size="small"
                  sx={{
                    backgroundColor: `${designSystem.colors.status.success}20`,
                    color: designSystem.colors.status.success,
                    fontWeight: 500,
                    fontSize: '0.65rem',
                    height: 18,
                  }}
                />
              </Box>
              
              {/* Action Buttons - Same row as name */}
              {actionButtons && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 0.75, 
                  alignItems: 'center', 
                  flexShrink: 0,
                }}>
                  {actionButtons}
                </Box>
              )}
            </Box>
            
            {/* Second Row: Demographics (CNP, DOB, Sex) */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1.5, sm: 2 }, 
              mt: 0.5,
              flexWrap: 'wrap'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BadgeIcon sx={{ fontSize: 12, color: colors.text.secondary }} />
                <Typography sx={{ color: colors.text.primary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {patient.cnp || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 12, color: colors.text.secondary }} />
                <Typography sx={{ color: colors.text.primary, fontSize: '0.7rem' }}>
                  {formatDOB(patient.dob)}{patient.dob && ` (${calculateAge(patient.dob)})`}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {sexIcon && React.cloneElement(sexIcon, { sx: { fontSize: 12, color: sexColor } })}
                <Typography sx={{ color: sexColor, fontWeight: 500, fontSize: '0.7rem' }}>
                  {sexLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Stats Footer - RESPONSIVE */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 0,
          borderTop: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated,
        }}
      >
        {/* Studii - Clickable */}
        <StatItem
          icon={<StudyIcon />}
          value={patient.studies_count}
          label="STUDII"
          color={colors.accent.primary}
          colors={colors}
          onClick={onStudiesClick}
          clickable={!!onStudiesClick}
        />

        {/* Rapoarte - Clickable */}
        <StatItem
          icon={<ReportIcon />}
          value={patient.reports_count}
          label="RAPOARTE"
          color={designSystem.colors.status.success}
          colors={colors}
          onClick={onReportsClick}
          clickable={!!onReportsClick}
        />

        {/* Documente - Clickable */}
        <StatItem
          icon={<DocumentIcon />}
          value={patient.documents_count}
          label="DOCUMENTE"
          color={designSystem.colors.status.info}
          colors={colors}
          onClick={onDocumentsClick}
          clickable={!!onDocumentsClick}
        />

        {/* Ultimul Studiu - Not clickable */}
        <StatItem
          icon={<ScheduleIcon />}
          value={getRelativeTime(patient.last_study_date)}
          label="ULTIMUL STUDIU"
          color={colors.text.secondary}
          colors={colors}
          isText
        />
      </Box>
    </Box>
  )
}

// Mini component pentru stat items - COMPACT
interface StatItemProps {
  icon: React.ReactNode
  value: number | string
  label: string
  color: string
  colors: any
  isText?: boolean
  onClick?: () => void
  clickable?: boolean
}

const StatItem: React.FC<StatItemProps> = ({ icon, value, label, color, colors, isText, onClick, clickable }) => (
  <Tooltip title={clickable ? `Click pentru a vedea ${label.toLowerCase()}` : label} arrow>
    <Box
      onClick={onClick}
      sx={{
        flex: { xs: '1 1 50%', sm: 1 },
        minWidth: { xs: '50%', sm: 'auto' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 0.5, sm: 1 },
        py: { xs: 0.75, sm: 1 },
        px: { xs: 0.5, sm: 1 },
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        borderRight: { xs: 'none', sm: `1px solid ${colors.border.default}` },
        borderBottom: { xs: `1px solid ${colors.border.default}`, sm: 'none' },
        '&:nth-of-type(2n)': {
          borderRight: { xs: 'none', sm: `1px solid ${colors.border.default}` },
        },
        '&:nth-of-type(n+3)': {
          borderBottom: { xs: 'none', sm: 'none' },
        },
        '&:last-child': {
          borderRight: 'none',
          borderBottom: 'none',
        },
        '&:hover': clickable ? {
          backgroundColor: alpha(color, 0.12),
          transform: 'translateY(-1px)',
        } : {
          backgroundColor: alpha(colors.accent.primary, 0.05),
        }
      }}
    >
      {React.cloneElement(icon as React.ReactElement, { 
        sx: { fontSize: { xs: 14, sm: 16 }, color: color } 
      })}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: isText ? colors.text.primary : color,
          fontSize: isText ? { xs: '0.65rem', sm: '0.75rem' } : { xs: '0.8rem', sm: '0.9rem' },
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: colors.text.secondary,
          fontSize: { xs: '0.55rem', sm: '0.6rem' },
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          display: { xs: 'none', sm: 'block' }
        }}
      >
        {label}
      </Typography>
    </Box>
  </Tooltip>
)

export default PatientHeroCard
